/* eslint-disable react-refresh/only-export-components */
import { useGetTableData } from "@/queries/data-center/data-center.api";
import type { Correction } from "@/queries/formaly-request/formaly-request.api";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import { cn } from "@/utils/theme";
import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";

const formatNumberValue = (val: unknown): string => {
  const num = Number(val);
  if (isNaN(num)) return String(val ?? "");
  return num.toLocaleString("en-US", { maximumFractionDigits: 10 });
};

export const getDataTypeIcon = (dataType: string): React.ReactNode => {
  switch (dataType) {
    case "textarea":
      return <Icon type="far" name="paragraph" />;
    case "identity":
    case "passport_number":
    case "passport":
    case "dui_nit":
      return <Icon type="far" name="id-card" />;
    case "select":
      return <Icon type="far" name="list" />;
    case "file":
      return <Icon type="far" name="file-upload" />;
    case "switch":
      return <Icon type="far" name="toggle-on" />;
    case "string":
      return <Icon type="far" name="text" />;
    case "number":
      return <Icon type="far" name="hashtag" />;
    case "date":
    case "datetime":
      return <Icon type="far" name="calendar-clock" />;
    case "date_range":
    case "datetime_range":
      return <Icon type="far" name="calendars" />;
    case "paragraph":
      return <Icon type="far" name="paragraph" />;
    case "list":
      return <Icon type="far" name="list" />;
    case "email":
      return <Icon type="far" name="envelope" />;
    case "phone":
      return <Icon type="far" name="phone" />;
    case "files":
      return <Icon type="far" name="file-upload" />;
    case "interoperability":
      return <Icon type="far" name="diagram-nested" />;
    default:
      return <Icon type="fas" name="text" />;
  }
};

export type Fields =
  FormDetailResponse["steps"][number]["sections"][number]["fields"][number];

const columnHelper = createColumnHelper<Fields>();
export const tableColumns = ({
  corrections,
  onCorrection,
  onOpenCorrectionModal,
  onShowFieldHistory,
  currentGroupId,
  lockedApprovedFields,
  isReadOnly = false,
  existingCorrection,
  groupsCount,
  nonPreventableFields,
}: {
  onOpenCorrectionModal: (fieldId: string) => void;
  onCorrection: (fieldId: string, status: "DENIED" | "APPROVED") => void;
  onShowFieldHistory: (field: Fields) => void;
  corrections: Map<string, { status: "DENIED" | "APPROVED"; reason?: string }>;
  currentGroupId?: string | null;
  lockedApprovedFields?: Set<string>;
  nonPreventableFields?: Set<string>;
  isReadOnly?: boolean;
  existingCorrection?: Correction;
  groupsCount: number;
}) => {
  return [
    columnHelper.accessor("name", {
      header: "Tipo de dato",
      meta: { className: "w-[353px] min-w-[353px] max-w-[353px] align-top" },
      cell: (info) => {
        if (!info.getValue()) {
          return <p>&nbsp;</p>;
        }
        const field = info.row.original;
        return (
          <div className="w-full flex items-center gap-4">
            {getDataTypeIcon(field.type)}
            <div className="w-full">
              <p className="w-full text-body-loud font-semibold truncate text-size-body max-w-[90%]">
                {field.label}
              </p>
              <p className="text-info text-xs truncate">
                {field.required ? "Obligatorio" : "Opcional"}
              </p>
              {!field?.is_preventable && (
                <p className="text-warning font-normal text-xs">
                  {" "}
                  (No Observable)
                </p>
              )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("filled_fields", {
      header: "Valor",
      meta: { className: "min-w-[351px] w-full break-words align-center" },
      cell: (info) => {
        const fieldArr = info.getValue();
        if (!fieldArr || fieldArr.length === 0) {
          return <p>&nbsp;</p>;
        }

        const filledFieldCount = fieldArr.length - 1;
        const value = fieldArr[filledFieldCount]?.value;
        const field = info.row.original;

        const obj = info.row.original;

        let content: React.ReactNode = (
          <p className="wrap-break-word whitespace-pre-wrap text-size-body">
            {(value || "").toString()}
          </p>
        );

        if (
          Object.prototype.hasOwnProperty.call(value || {}, "start") &&
          Object.prototype.hasOwnProperty.call(value || {}, "end")
        ) {
          const { start, end } = value as { start: string; end: string };
          content = (
            <p className="break-normal whitespace-pre-wrap text-size-body">
              {start} {" -> "} {end}
            </p>
          );
        } else if (
          obj.type === "select" &&
          obj.config.options_type === "manual"
        ) {
          if (obj.config.multiple) {
            const values = (value as Array<string>) || [];
            const result = values
              .map(
                (val) =>
                  obj.config.options.find(
                    (o: { value: string }) => o.value === val,
                  )?.label ?? null,
              )
              .filter(Boolean);

            content = (
              <p className="wrap-break-word whitespace-pre-wrap text-size-body">
                {result.join(", ")}
              </p>
            );
          } else {
            const option = obj.config.options.find(
              (o: { value: string }) => o.value === value,
            );
            content = option ? (
              <p className="wrap-break-word whitespace-pre-wrap text-size-body">
                {option.label}
              </p>
            ) : (
              <p>&nbsp;</p>
            );
          }
        } else if (obj.type === "file") {
          const files = fieldArr[filledFieldCount]?.files;
          content = (
            <ul className="flex gap-2 flex-wrap">
              {(files || []).map((f) => (
                <Badge
                  key={f.id}
                  variant="neutral"
                  className="max-w-90 cursor-pointer"
                  onClick={() =>
                    window.open(f.url, "_blank", "noopener,noreferrer")
                  }
                >
                  <span className="truncate flex-1 min-w-0">{f.file_name}</span>
                  <Icon type="fas" name="file" size="xs" className="shrink-0" />
                </Badge>
              ))}
            </ul>
          );
        } else if (
          obj.type === "dui_nit" &&
          obj.config.allow_select_document_type
        ) {
          const duiNITValue = value as unknown as {
            type: string;
            value: string;
          };
          content = duiNITValue ? (
            <p className="wrap-break-word whitespace-pre-wrap text-size-body">
              (
              {duiNITValue?.type === "passport"
                ? "PASAPORTE"
                : duiNITValue?.type.toUpperCase()}
              )&nbsp;{duiNITValue?.value}
            </p>
          ) : (
            <p>&nbsp;</p>
          );
        } else if (
          obj.type === "select" &&
          obj.config.options_type === "database" &&
          !obj.config.multiple
        ) {
          content = (
            <DataCenterValue
              table_name={obj.config.data_center_table}
              column_name={obj.config.data_center_column}
              table_source={obj.config.source}
              value={value as string}
            />
          );
        } else if (
          obj.type === "select" &&
          obj.config.options_type === "database" &&
          obj.config.multiple &&
          Array.isArray(value)
        ) {
          content = ((value as Array<string>) || []).map((val, idx, arr) => (
            <span key={val}>
              <DataCenterValue
                table_name={obj.config.data_center_table}
                column_name={obj.config.data_center_column}
                table_source={obj.config.source}
                value={val}
              />
              {idx < arr.length - 1 && ", "}
            </span>
          ));
        } else if (Array.isArray(value)) {
          content = (
            <div className="flex gap-2 flex-wrap p-3">
              {((value as Array<string>) || []).map((fileName, index) => (
                <Badge
                  key={"file_" + index}
                  size="sm"
                  shape="pill"
                  variant="neutral"
                  className="text-size-body-sm"
                >
                  {fileName}
                  <Icon type="fas" name="file" className="text-neutral-quiet" />
                </Badge>
              ))}
            </div>
          );
        } else if (typeof value === "boolean") {
          content = (
            <p className="wrap-break-word whitespace-pre-wrap text-size-body">
              {value ? "Sí" : "No"}
            </p>
          );
        } else if (obj.type === "number") {
          content = (
            <p className="wrap-break-word whitespace-pre-wrap text-size-body">
              {formatNumberValue(value)}
            </p>
          );
        }

        if (field.type === "interoperability") {
          const count = info.row.original.config.input_field.length || 0;
          const lastValue = fieldArr.slice(-count);
          content = (
            <div className="flex flex-col gap-2">
              {lastValue.map((filledField, index) => (
                <div
                  key={`${filledField.id}-${index}`}
                  className="flex flex-col"
                >
                  <p className="text-xs text-info font-semibold">
                    {filledField.label}:
                  </p>
                  <p className="break-normal whitespace-pre-wrap text-size-body">
                    {(filledField.value || "").toString()}
                  </p>
                </div>
              ))}
            </div>
          );
        }

        return (
          <div className="flex w-full items-center gap-2">
            <div className="min-w-0 flex-1">{content}</div>
            <Button
              size="icon"
              variant="quiet"
              type="button"
              aria-label="correction-history"
              className="shrink-0"
              onClick={() => onShowFieldHistory(field)}
            >
              <Icon name="clock" type="far" />
            </Button>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      meta: {
        className: "w-[48px] border-transparent border-x-2 align-center",
      },
      cell: (info) => {
        const filledFields = info.row.original.filled_fields;
        const isPreventable = info.row.original.is_preventable;
        const filledFieldCount =
          filledFields && filledFields.length > 0
            ? filledFields.length - 1
            : -1;
        const filledFieldId =
          filledFieldCount >= 0
            ? info.row.original.filled_fields?.[filledFieldCount]?.id
            : undefined;

        if (nonPreventableFields?.has(filledFieldId || "") || !isPreventable) {
          return;
        }

        // Si es modo solo lectura, mostrar la corrección existente
        if (isReadOnly && existingCorrection) {
          if (!filledFieldId) {
            return null;
          }
          const fieldCorrection = existingCorrection.field_corrections?.find(
            (fc) => fc.filled_field_id === filledFieldId,
          );

          if (fieldCorrection) {
            const isDenied = fieldCorrection.status === "DENIED";
            const hasComments =
              fieldCorrection.comments && fieldCorrection.comments.length > 0;

            return (
              <div className="relative">
                {isDenied && hasComments && (
                  <Badge
                    size="xs"
                    variant="danger"
                    fill="solid"
                    className="rounded-full size-5 flex items-center justify-center absolute -top-3 -right-2 cursor-pointer"
                    onClick={() => onOpenCorrectionModal(filledFieldId)}
                  >
                    <Icon
                      name="comment"
                      type="fas"
                      className="text-2xs text-on-solid"
                    />
                  </Badge>
                )}
                <Badge
                  shape="square"
                  size="sm"
                  variant={isDenied ? "danger" : "success"}
                  className="size-6 flex items-center justify-center rounded-lg cursor-default"
                >
                  <Icon
                    name={isDenied ? "times" : "check"}
                    type="fas"
                    className={cn(
                      "text-xs",
                      isDenied ? "text-danger-quiet" : "text-success-quiet",
                    )}
                  />
                </Badge>
              </div>
            );
          }

          // Si no hay corrección para este campo en modo solo lectura, no mostrar nada
          return null;
        }

        // Usar el ID del campo como alternativa cuando no hay un campo llenado
        const fieldIdentifier =
          filledFieldId || `field_${info.row.original.id}`;

        // Cuando hay múltiples grupos (currentGroupId está definido), no bloquear botones
        // basados en aprobaciones previas - cada grupo debe poder aprobar independientemente

        // No bloquear ningún campo cuando hay múltiples grupos o cuando se visualiza en modo no solo lectura
        // Cada ciclo debe ser independiente - las aprobaciones previas no deben bloquear nuevas evaluaciones
        // let hasPreviousCorrectionApproved = false;
        const hasPreviousCorrectionApproved =
          lockedApprovedFields?.has(filledFieldId || "") || false;

        // if (groupsCount === 1) {
        //   hasPreviousCorrectionApproved =
        //     lockedApprovedFields?.has(filledFieldId || "") || false;
        // }

        // Verificar si el campo fue aprobado en un ciclo anterior y no puede ser denegado
        // PERO: cuando hay grupos (currentGroupId), no bloquear ningún campo
        const isLockedApproved =
          currentGroupId && groupsCount > 1
            ? false
            : filledFieldId
              ? lockedApprovedFields?.has(filledFieldId) || false
              : false;

        const correction = corrections.get(fieldIdentifier);
        return (
          <div
            className={cn(
              "flex gap-3",
              hasPreviousCorrectionApproved && "justify-start",
            )}
          >
            <Badge
              shape="square"
              size="sm"
              className="size-6 flex items-center justify-center rounded-lg cursor-pointer"
              variant={
                correction?.status === "APPROVED" ||
                hasPreviousCorrectionApproved
                  ? "success"
                  : "neutral"
              }
              onClick={() => {
                if (hasPreviousCorrectionApproved) return;
                onCorrection(fieldIdentifier, "APPROVED");
              }}
            >
              <Icon
                name="check"
                type="fas"
                className={cn(
                  "text-xs",
                  correction?.status === "APPROVED" && "text-success-quiet",
                )}
              />
            </Badge>
            {!hasPreviousCorrectionApproved && !isLockedApproved && (
              <div className="relative">
                {correction?.status === "DENIED" && (
                  <Badge
                    size="xs"
                    variant="danger"
                    fill="solid"
                    className="rounded-full size-5 flex items-center justify-center absolute -top-3 -right-2"
                    onClick={() => {
                      onOpenCorrectionModal(fieldIdentifier);
                    }}
                  >
                    <Icon
                      name="comment"
                      type="fas"
                      className="text-2xs text-on-solid"
                    />
                  </Badge>
                )}
                <Badge
                  shape="square"
                  size="sm"
                  variant={
                    correction?.status === "DENIED" ? "danger" : "neutral"
                  }
                  onClick={() => {
                    onCorrection(fieldIdentifier, "DENIED");
                  }}
                  className="size-6 flex items-center justify-center rounded-lg cursor-pointer"
                >
                  <Icon
                    name="times"
                    type="fas"
                    className={cn(
                      "text-xs",
                      correction?.status === "DENIED" && "text-danger-quiet",
                    )}
                  />
                </Badge>
              </div>
            )}
          </div>
        );
      },
    }),
  ] as ColumnDef<Fields>[];
};

const DataCenterValue = ({
  value,
  table_name,
  column_name,
  table_source,
}: {
  table_name: string;
  column_name: string;
  table_source: string;
  value?: string | number | string[];
}) => {
  const { data } = useGetTableData(table_name!, table_source, 1, [
    {
      name: "id",
      value: value as string,
      operator: "EQUALS",
      type: "STRING",
    },
  ]);
  if (!data) return "Cargando...";

  const record = data?.data?.data?.[0];
  if (record) {
    return (
      <span className="text-body text-size-body">
        {record[column_name as keyof typeof record] as string}
      </span>
    );
  }

  return <span className="text-body">{"—"}</span>;
};
